import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useAppSelector } from "store";
import { authNewSelectors } from "store/reducers/auth-new";

type RecaptchaContextType = {
  tokenGet: (action: string) => Promise<string>;
};

const RecaptchaContext = createContext<RecaptchaContextType>({
  tokenGet: async () => "",
});

export const useRecaptchaContext = () => {
  return useContext(RecaptchaContext);
};

export const RecaptchaProvider = ({ children }: PropsWithChildren<{}>) => {
  const loadedRef = useRef(false);
  const isLoggedIn = useAppSelector(authNewSelectors.loggedInGet);

  useEffect(() => {
    if (isLoggedIn) return;

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA3_KEY}`;
    script.addEventListener("load", () => (loadedRef.current = true));
    document.body.appendChild(script);
  }, [isLoggedIn]);

  const context = useMemo<RecaptchaContextType>(() => {
    const waitUntilLoaded = () =>
      new Promise<void>((resolve) => {
        if (loadedRef.current) {
          resolve();
        } else {
          const interval = setInterval(() => {
            if (loadedRef.current) {
              clearInterval(interval);
              resolve();
            }
          }, 100);
        }
      });

    return {
      tokenGet: async (action: string) => {
        await waitUntilLoaded();

        return new Promise((resolve, reject) => {
          grecaptcha.ready(() => {
            grecaptcha
              .execute(process.env.REACT_APP_RECAPTCHA3_KEY!, { action })
              .then((token: string) => {
                resolve(token);
              })
              .catch((error: any) => {
                reject(error);
              });
          });
        });
      },
    };
  }, []);

  return (
    <RecaptchaContext.Provider value={context}>
      {children}
    </RecaptchaContext.Provider>
  );
};
